import { useState } from 'react'
import { Stack, TableBody, TableHead, TableRow } from '@mui/material'

import { Card } from '@repo/ui'
import { InfoTooltip } from 'components/feedback/InfoTooltip'
import { ViewToggle, type ViewToggleOption } from 'components/input/ViewToggle/ViewToggle'
import type { WebsiteAnalyticsOverview, WebsiteSourceRow } from 'services'

import {
  AUDIENCE_LABELS,
  conversion,
  formatCount,
  formatPct,
  referrerLabel,
} from '../websiteAnalyticsModel'
import { Cell, EmptyRow, HeadCell, SectionNote, StatTable } from './shared'

type Dimension = 'referrers' | 'paths' | 'campaigns' | 'devices'

const OPTIONS: readonly ViewToggleOption<Dimension>[] = [
  { value: 'referrers', label: 'Referrer' },
  { value: 'campaigns', label: 'Campaign' },
  { value: 'paths', label: 'Page' },
  { value: 'devices', label: 'Device' },
]

const TOP = 25

const SourceTable = ({ rows, label }: { rows: WebsiteSourceRow[]; label: string }) => (
  <StatTable>
    <TableHead>
      <TableRow>
        <HeadCell>{label}</HeadCell>
        <HeadCell align="right">Visitors</HeadCell>
        <HeadCell align="right">Pageviews</HeadCell>
        <HeadCell align="right">Form submitted</HeadCell>
        <HeadCell align="right">Signups*</HeadCell>
        <HeadCell align="right">Clients*</HeadCell>
        <HeadCell align="right">
          Signups / visitors
          <InfoTooltip term="website-source-signup-rate" />
        </HeadCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {rows.length === 0 ? (
        <EmptyRow colSpan={7} text="No traffic recorded for this range." />
      ) : (
        rows.slice(0, TOP).map((r) => (
          <TableRow key={r.value || '(direct)'}>
            <Cell>{label === 'Referrer' ? referrerLabel(r.value) : r.value || '—'}</Cell>
            <Cell align="right">{formatCount(r.visitors)}</Cell>
            <Cell align="right">{formatCount(r.pageviews)}</Cell>
            <Cell align="right">{formatCount(r.formSubmitted)}</Cell>
            <Cell align="right">{formatCount(r.signups)}</Cell>
            <Cell align="right">{formatCount(r.clients)}</Cell>
            <Cell align="right" strong>
              {formatPct(conversion(r.visitors, r.signups))}
            </Cell>
          </TableRow>
        ))
      )}
    </TableBody>
  </StatTable>
)

export const WebsiteSourcesCard = ({ overview }: { overview: WebsiteAnalyticsOverview }) => {
  const [dim, setDim] = useState<Dimension>('referrers')
  const { breakdown } = overview

  return (
    <Card
      title="Sources"
      action={
        <ViewToggle options={OPTIONS} value={dim} onChange={setDim} ariaLabel="Source dimension" />
      }
    >
      <Stack spacing={3} sx={{ mt: 4 }}>
        {dim === 'campaigns' ? (
          <>
            <SectionNote>
              UTM source / campaign of each lead’s FIRST visit ({AUDIENCE_LABELS.consented} only —
              Vercel’s UTM breakdown needs a paid add-on we do not have), plus the{' '}
              <code>source</code> recorded on each signup.
            </SectionNote>
            <StatTable>
              <TableHead>
                <TableRow>
                  <HeadCell>Campaign</HeadCell>
                  <HeadCell align="right">Leads first seen</HeadCell>
                  <HeadCell align="right">Signups</HeadCell>
                  <HeadCell align="right">Clients</HeadCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {breakdown.campaigns.length === 0 ? (
                  <EmptyRow colSpan={4} text="No campaign-tagged leads or signups in this range." />
                ) : (
                  breakdown.campaigns.slice(0, TOP).map((r) => (
                    <TableRow key={r.value}>
                      <Cell>{r.value}</Cell>
                      <Cell align="right">{formatCount(r.leads)}</Cell>
                      <Cell align="right">{formatCount(r.signups)}</Cell>
                      <Cell align="right">{formatCount(r.clients)}</Cell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </StatTable>
          </>
        ) : (
          <>
            <SectionNote>
              Visitors, pageviews and form submissions: {AUDIENCE_LABELS.all}. *Signups and
              clients are attributed to a lead’s first referrer / landing page, which only the
              audience “{AUDIENCE_LABELS.consented}” carries. They undercount, so the
              rate is a floor, not an estimate.
              {dim === 'paths'
                ? ' Page counts are every page viewed, not only landing pages.'
                : ''}
            </SectionNote>
            <SourceTable
              rows={breakdown[dim]}
              label={dim === 'referrers' ? 'Referrer' : dim === 'paths' ? 'Page' : 'Device'}
            />
          </>
        )}
      </Stack>
    </Card>
  )
}
